// Typed API client for the Cloudflare Worker.
import { isApiError, APP_ID, WORKER_BASE_URL, type AuthResponse, type User, type ApiError } from '@funderpro/shared';

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  authToken?: string;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = path.startsWith('http') ? path : `${WORKER_BASE_URL}${path}`;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(opts.authToken ? { Authorization: `Bearer ${opts.authToken}` } : {}),
    'X-App-Id': APP_ID,
    ...(opts.headers || {}),
  };
  const res = await fetch(url, {
    ...opts,
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  let data: unknown;
  try { data = await res.json(); } catch { data = null; }
  if (!res.ok) {
    const err = (data && isApiError(data) ? data : { error_type: 'HTTPException', message: res.statusText } as ApiError);
    throw Object.assign(new Error(err.message), { status: res.status, body: err });
  }
  return data as T;
}

export const API = {
  base: WORKER_BASE_URL,
  login: (email: string, password: string) =>
    apiFetch<AuthResponse>(`/api/apps/${APP_ID}/auth/login`, { method: 'POST', body: { email, password } }),
  register: (email: string, password: string, full_name?: string) =>
    apiFetch<{ success: boolean; user_id: string; dev_otp?: string }>(
      `/api/apps/${APP_ID}/auth/register`, { method: 'POST', body: { email, password, full_name } }
    ),
  verifyOtp: (email: string, otp_code: string) =>
    apiFetch<AuthResponse>(`/api/apps/${APP_ID}/auth/verify-otp`, { method: 'POST', body: { email, otp_code } }),
  me: (token: string) => apiFetch<User>(`/api/apps/${APP_ID}/auth/me`, { authToken: token }),
  health: () => apiFetch<{ ok: boolean; time: string }>(`/api/health`),
  // Entity CRUD — auto-injects the bearer token from localStorage
  list: <T = any>(entity: string, params?: Record<string, string | number | undefined>) => {
    const qs = params && Object.keys(params).length ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][]).toString() : '';
    return apiFetch<T[]>(`/api/apps/${APP_ID}/entities/${entity}${qs}`, { authToken: getToken() || undefined });
  },
  get: <T = any>(entity: string, id: string) =>
    apiFetch<T>(`/api/apps/${APP_ID}/entities/${entity}/${id}`, { authToken: getToken() || undefined }),
  create: <T = any>(entity: string, body: Record<string, unknown>) =>
    apiFetch<T>(`/api/apps/${APP_ID}/entities/${entity}`, { method: 'POST', body, authToken: getToken() || undefined }),
  update: <T = any>(entity: string, id: string, body: Record<string, unknown>) =>
    apiFetch<T>(`/api/apps/${APP_ID}/entities/${entity}/${id}`, { method: 'PATCH', body, authToken: getToken() || undefined }),
  remove: <T = any>(entity: string, id: string) =>
    apiFetch<T>(`/api/apps/${APP_ID}/entities/${entity}/${id}`, { method: 'DELETE', authToken: getToken() || undefined }),
};

function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('fp_access_token');
}

// Backwards compat with the old `api` namespace
export const api = API;
